import { Button } from '@progress/kendo-react-buttons'
import DataTable from 'components/DataTable'
import FilterSelector from 'components/DropdownSelectors/FilterSelector'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import { MembershipApiItem } from 'types/membership'
import { MembershipClient } from 'types/membership-client'
import { classNames } from 'utils/dom-helpers'
import { useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import {
    toYmd,
} from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import {
    MembershipFilters,
    MembershipStatus,
} from 'components/DropdownSelectors/common'
import MembershipCancelModal from './MembershipCancelModal'
import { mapMembershipAndClientMembership } from 'services/memberships-client'
import { useTenantContext } from 'context/TenantContext'

function EmptyMemberships({ handleAdd }: { handleAdd(): any }) {
    return (
        <div className="text-center py-10">
            <div className="rounded-full bg-gray-100 w-20 h-20 inline-block leading-loose text-center align-middle">
                <i className="text-gray-400 feather feather-calendar text-4xl leading-loose" />
            </div>
            <div className="text-gray-400 block mt-4 mx-auto">
                <Translation content_key="no_client_memberships" />
            </div>
            <button
                className="bg-primary rounded-lg border-gray-200 py-3 px-12 border shadow-xl mt-6 text-white"
                onClick={handleAdd}
            >
                <Translation content_key="add_membership" />
            </button>
        </div>
    )
}

function RenderMemberships({
    filtered_membership_clients,
    translations,
    membership_filter,
    setMembershipFilter,
    handleAdd,
    handleEdit,
    handleCancel,
}: {
    filtered_membership_clients: MembershipClient[]
    translations: Record<string, string>
    membership_filter: MembershipFilters
    setMembershipFilter(filter: MembershipFilters): any
    handleAdd(): void
    handleEdit(idx: number): void
    handleCancel(idx: number): void
}) {
    const rows: HTMLTableRowElement[] = (
        filtered_membership_clients || []
    ).map(
        (
            {
                id,
                name,
                type,
                location,
                details,
                validity,
                status,
                renewal,
                renewal_or_expiry_date,
            }: MembershipClient,
            idx
        ) =>
            (
                <tr
                    key={id}
                    className={classNames(
                        idx % 2 ? 'bg-gray-50' : '',
                        'hover:bg-blue-300 hover:bg-opacity-30'
                    )}
                >
                    <td
                        className="px-3 py-3 align-top"
                        onClick={() => handleEdit(idx)}
                    >
                        <span className="block font-bold">{name}</span>
                        <span className="capitalize">
                            <Translation content_key={type} />
                        </span>
                        <span className={location ? '' : 'hidden'}>
                            {' '}
                            / {location}
                        </span>
                    </td>
                    <td
                        className="px-3 py-3 align-top"
                        onClick={() => handleEdit(idx)}
                    >
                        <span className="block font-bold">{details}</span>
                        <span className="block">
                            {validity}{' '}
                            <Translation content_key="validity" />
                        </span>
                    </td>
                    <td
                        className="px-3 py-3 align-top"
                        onClick={() => handleEdit(idx)}
                    >
                        <span>
                            {renewal_or_expiry_date
                                ? toYmd(renewal_or_expiry_date)
                                : renewal}
                        </span>
                    </td>
                    <td
                        className="px-3 py-3 align-top"
                        onClick={() => handleEdit(idx)}
                    >
                        <span
                            className={classNames(
                                status == 'active'
                                    ? 'bg-primary-lighter text-gray-800'
                                    : 'bg-orange-800 text-white',
                                'px-4 py-1 inline-flex text-xs leading-5 font-bold rounded-full uppercase text-center'
                            )}
                        >
                            <Translation content_key={status} />
                        </span>
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                        <Button
                            className="bg-primary-lighter bg-opacity-50 rounded mr-2"
                            iconClass="feather-edit mr-1"
                            onClick={() => handleEdit(idx)}
                        >
                            <Translation content_key="edit" />
                        </Button>
                        <Button
                            className={classNames(
                                'bg-red-100 text-red-500 rounded mr-2',
                                status == 'cancelled' ? 'hidden' : ''
                            )}
                            iconClass="feather-archive"
                            onClick={() => handleCancel(idx)}
                        >
                            <Translation content_key="cancel" />
                        </Button>
                    </td>
                </tr>
            ) as unknown as HTMLTableRowElement
    )

    const headers = [
        'membership',
        'details',
        'renewal_expiry_date',
        'status',
        'actions',
    ].map((key) => {
        return {
            label: getTranslation(key, translations),
            classNames:
                `px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider`,
        }
    })

    return (
        <div className="flex flex-col">
            <div className="align-middle inline-block min-w-full">
                <div className="flex justify-between">
                    <div className="flex gap-3">
                        {/* TODO: Add Date Filter here */}
                        <FilterSelector
                            items={[]}
                            is_membership_filter_enabled={true}
                            membership_filter={membership_filter}
                            setMembershipFilter={setMembershipFilter}
                        />
                    </div>
                    <button
                        onClick={handleAdd}
                        className="flex items-center bg-primary-lighter text-primary btn-gap  text-sm rounded-lg mb-3"
                    >
                        <i className="feather-plus text-sm mr-2" />
                        <Translation
                            content_key="add_membership"
                            translations={translations}
                        />
                    </button>
                </div>
                <div className="mt-4">
                    <DataTable columns={headers} rows={rows} />
                </div>
            </div>
        </div>
    )
}

export function Memberships() {
    const { translations } = useAppContext()
    const { all_memberships, tenant } = useTenantContext()
    const {
        CreateClientModal,
        MembershipAddModal,
        MembershipEditModal,
        PurchaseSummaryModal,
    } = useAuth()
    const [membership_clients, setMembershipClients] = useState<
        MembershipClient[]
    >([])
    const [filtered_membership_clients, setFilteredMembershipClients] =
        useState<MembershipClient[]>([])
    const [memberships, setMemberships] = useState<MembershipApiItem[]>([])
    const [membership_filter, setMembershipFilter] = useState(
        MembershipFilters.ACTIVE
    )
    const [to_cancel, setToCancel] = useState<MembershipClient>()
    const [show_cancel_modal, toggleCancelModal] = useState(false)

    // Membership Client List
    const {
        data: memberships_client_api_response,
        doFetch: getMembershipClients,
    } = useFetch(
        `/v1/memberships/clients?clientId=${CreateClientModal.attributes?.id}&size=500`,
        FetchMethods.GET,
        !!CreateClientModal?.attributes?.id
    )

    // Locations
    const { data: locations_api_response } = useFetch(
        `/v1/locations/tenant-id/?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id
    )

    useEffect(() => {
        if (
            !all_memberships.length ||
            !memberships_client_api_response.content?.length ||
            !locations_api_response.content?.length
        ) {
            return
        }

        let fetched_memberships: MembershipApiItem[] = all_memberships
        let fetched_membership_clients: MembershipClient[] =
            memberships_client_api_response.content

        fetched_membership_clients = fetched_membership_clients.map(
            (mc: MembershipClient) => {
                let membership = fetched_memberships.filter(
                    (m) => m.id == mc.membershipId
                )[0]
                if (membership) {
                    mapMembershipAndClientMembership(membership, mc)
                    const location = locations_api_response.content?.find(
                        (l: MembershipApiItem) =>
                            l.id == membership.primaryLocationId
                    )
                    mc.location = location?.name || 'All'
                }
                return mc
            }
        )
        setMemberships(fetched_memberships)
        setMembershipClients(fetched_membership_clients)
        setFilteredMembershipClients(fetched_membership_clients)

        CreateClientModal.setAttributes({
            ...CreateClientModal.attributes,
            membership_clients: memberships_client_api_response?.content
                || []
        })
    }, [
        memberships_client_api_response,
        all_memberships,
        locations_api_response,
    ])

    useEffect(() => {
        if (
            PurchaseSummaryModal.attributes?.refetch ||
            MembershipEditModal.attributes?.refetch
        ) {
            PurchaseSummaryModal.setAttributes({
                refetch: false,
            })
            MembershipEditModal.setAttributes({
                refetch: false,
            })
            getMembershipClients()
        }
    }, [
        PurchaseSummaryModal.attributes?.refetch,
        MembershipEditModal.attributes?.refetch,
    ])

    useEffect(() => {
        switch (membership_filter) {
            case MembershipFilters.ACTIVE:
                setFilteredMembershipClients(
                    (membership_clients || []).filter(
                        (mc) =>
                            mc.status.toUpperCase() ===
                            MembershipStatus.ACTIVE
                    )
                )
                break
            case MembershipFilters.INACTIVE:
                setFilteredMembershipClients(
                    (membership_clients || []).filter((mc) => {
                        const to_upper = mc.status.toUpperCase()
                        return (
                            to_upper === MembershipStatus.EXPIRED ||
                            to_upper === MembershipStatus.RENEWAL_FAILED_MAXED_OUT ||
                            to_upper === MembershipStatus.CANCELLED
                        )
                    })
                )
                break
            case MembershipFilters.RETRYING:
                setFilteredMembershipClients(
                    (membership_clients || []).filter((mc) => {
                        const to_upper = mc.status.toUpperCase()
                        return (
                            to_upper ===
                            MembershipStatus.RENEWAL_FAILED_RETRYING
                        )
                    })
                )
                break
            default:
                /* also for MembershipFilters.ALL*/
                setFilteredMembershipClients(membership_clients || [])
                break
        }
    }, [membership_filter])

    function handleAdd() {
        MembershipAddModal.open()
    }

    function handleEdit(idx: number) {
        const membership_client = filtered_membership_clients[idx]
        const selected_membership = memberships.find(
            (item) => item.id === membership_client.membershipId
        )
        MembershipEditModal.setAttributes({
            ...membership_client,
            selected_membership: selected_membership as MembershipApiItem,
        })
        MembershipEditModal.open()
    }

    const handleCancel = (idx: number) => {
        let membership_client = filtered_membership_clients[idx]
        if (membership_client) {
            setToCancel(membership_client)
            toggleCancelModal(true)
        }
    }

    return (
        <div>
            {filtered_membership_clients.length == 0 &&
                membership_filter === MembershipFilters.ALL ? (
                <EmptyMemberships handleAdd={handleAdd} />
            ) : (
                <RenderMemberships
                    filtered_membership_clients={
                        filtered_membership_clients
                    }
                    translations={translations}
                    membership_filter={membership_filter}
                    setMembershipFilter={setMembershipFilter}
                    handleAdd={handleAdd}
                    handleEdit={handleEdit}
                    handleCancel={handleCancel}
                />
            )}
            <MembershipCancelModal
                is_open={show_cancel_modal}
                membership_client={to_cancel}
                onConfirm={() => {
                    toggleCancelModal(false)
                    getMembershipClients()
                }}
                onCancel={() => {
                    toggleCancelModal(false)
                }}
            />
        </div>
    )
}

export default Memberships
